import { useContext, useState, useEffect, useRef } from "react";
import { Box, Typography, Button, Tooltip, Stack } from "@mui/material";
import { DateTime } from "luxon";
import { UserContext } from "../contexts/userContext";
import {
  LOCALE,
  MARKETPLACE,
  INTERNAL,
  MERCHANT,
  ORDER_STATUS,
  COUNTRIES,
  isMacroClient,
} from "../utils/constants";
import { exportDashboardPdf } from "../utils/pdfExport";
import Loader from "../components/Loader";
import { useDashboardFilters } from "../customHooks/useDashboardFilters";
import { useDashboardFilterOptions } from "../customHooks/useDashboardFilterOptions";
import { useDashboardSection } from "../customHooks/useDashboardSection";
import DashboardFilterBar from "../components/dashboard/DashboardFilterBar";
import DashboardSectionContainer from "../components/dashboard/DashboardSectionContainer";
import DashboardSectionGroup from "../components/dashboard/DashboardSectionGroup";
import DashboardSectionRow from "../components/dashboard/DashboardSectionRow";
import ExecutiveSummary from "../components/dashboard/sections/ExecutiveSummary";
import ShipmentsByStatus from "../components/dashboard/sections/ShipmentsByStatus";
import ShipmentsByType from "../components/dashboard/sections/ShipmentsByType";
import TopMerchants from "../components/dashboard/sections/TopMerchants";
import OperatorsPerformance from "../components/dashboard/sections/OperatorsPerformance";
import TimingKpis from "../components/dashboard/sections/TimingKpis";
import SlaTrend from "../components/dashboard/sections/SlaTrend";
import ServiceQuality from "../components/dashboard/sections/ServiceQuality";
import CostSummary from "../components/dashboard/sections/CostSummary";
import CostByOperator from "../components/dashboard/sections/CostByOperator";
import CostTrend from "../components/dashboard/sections/CostTrend";
import Geography from "../components/dashboard/sections/Geography";
// GeographyMap (mapa coroplético) queda reservado para la V2: el componente
// está listo y desacoplado, solo le falta la geometría de provincias por país.
// Ver docs/DASHBOARD_GEOGRAPHY_V2.md. Se reemplaza por la tabla-ranking
// estilizada (Geography), más simple y sin dependencia de geometría.
// import GeographyMap from "../components/dashboard/GeographyMap";
import { getProvinceLabel, IProvinceLabel } from "../utils/common";

import {
  fetchDashboardSummary,
  fetchShipmentsByStatus,
  fetchShipmentsByType,
  fetchTopMerchants,
  fetchOperatorsPerformance,
  fetchTimingSummary,
  fetchSlaTrend,
  fetchServiceQuality,
  fetchCostSummary,
  fetchCostByOperator,
  fetchCostTrend,
  fetchGeography,
} from "../requests/dashboardRequests";
import {
  IDashboardFilters,
  IDashboardSummary,
  IShipmentByStatus,
  IShipmentByType,
  ITopMerchant,
  IOperatorPerformance,
  ITimingSummary,
  ISlaTrendItem,
  IServiceQuality,
  ICostSummary,
  ICostByOperator,
  ICostTrendItem,
  IGeographyItem,
  IDashboardFilterOptions,
} from "../interfaces/dashboard";

// Refleja (duck-typed, sin importar nada de useDashboardSection.ts) la forma
// que devuelve ese hook, para poder tipar los props de cada grupo extraído
// sin tocar el hook en sí.
interface ISection<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

// "Top merchants" solo tiene sentido para entidades que agrupan varios
// merchants (marketplace) o para la vista interna cross-entidad. Un merchant o
// un ecommerce (vendedor único) no tienen otros merchants que rankear.
const entityHasMerchants = (entity?: string): boolean =>
  entity === MARKETPLACE || entity === INTERNAL;

// El filtro de Merchant solo aplica a marketplaces. El interno es cross-entidad
// (scope="all") y ahí el merchant_id no se filtra para no mezclar homónimos.
const entityHasMerchantFilter = (entity?: string): boolean =>
  entity === MARKETPLACE;

// El filtro de Cliente solo aplica al usuario interno (cross-entidad): default
// "Todos" = scope="all"; al elegir un cliente, drill-down a ese client_id. En
// Macro (mono-cliente) no aplica: el scope resuelve siempre al único cliente, así
// que se oculta y no se envía client_id.
const entityHasClientFilter = (entity?: string): boolean =>
  entity === INTERNAL && !isMacroClient();

// El filtro de País solo aplica al usuario interno (cross-país): default "ARG".
// No mezcla países en las métricas: siempre consulta uno. En Macro (mono-país,
// sin asignaciones de país) no aplica: se oculta y no se envía `country` (enviarlo
// filtraría por un valor inexistente).
const entityHasCountryFilter = (entity?: string): boolean =>
  entity === INTERNAL && !isMacroClient();

// El rango del dashboard está acotado a un mes, así que "últimas 8 semanas" ya no
// aplica: el título refleja el período elegido. Con filtro de fechas la métrica
// muestra ese período (no un máximo de 8 semanas).
const buildCostTrendTitle = (from: string, to: string): string => {
  const fmt = (iso: string) => {
    const dt = DateTime.fromISO(iso);
    return dt.isValid ? dt.setLocale(LOCALE).toFormat("dd/MM") : iso;
  };
  return `Evolución del costo por envío (${fmt(from)} – ${fmt(to)})`;
};

const fmtDate = (iso: string): string => {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.setLocale(LOCALE).toFormat("dd/MM/yyyy") : iso;
};

// Resumen legible de los filtros activos para el encabezado del PDF: resuelve
// los ids a nombres usando las opciones cargadas del backend.
const buildPdfSubtitle = (
  filters: IDashboardFilters,
  options: IDashboardFilterOptions,
  provinceLabel: IProvinceLabel,
): string => {
  const parts = [`Período: ${fmtDate(filters.from)} – ${fmtDate(filters.to)}`];

  if (filters.carrier_id) {
    const carrier = options.carriers.find(
      (c) => c.carrier_id === filters.carrier_id,
    );
    parts.push(`Operador: ${carrier?.carrier_name ?? filters.carrier_id}`);
  }
  if (filters.status) {
    const status = ORDER_STATUS.find((s) => s.id === filters.status);
    parts.push(`Estado: ${status?.label ?? filters.status}`);
  }
  if (filters.province)
    parts.push(`${provinceLabel.singular}: ${filters.province}`);
  if (filters.client_id) {
    const client = options.clients.find(
      (c) => String(c.client_id) === filters.client_id,
    );
    parts.push(`Cliente: ${client?.clientName ?? filters.client_id}`);
  }
  if (filters.merchant_id) {
    const merchant = options.merchants.find(
      (m) => String(m.merchant_id) === filters.merchant_id,
    );
    parts.push(`Merchant: ${merchant?.merchant_name ?? filters.merchant_id}`);
  }
  if (filters.country) {
    parts.push(`País: ${COUNTRIES[filters.country] ?? filters.country}`);
  }

  return parts.join("  ·  ");
};

const useDashboardSections = (
  filters: IDashboardFilters,
  showTopMerchants: boolean,
) => ({
  summary: useDashboardSection(fetchDashboardSummary, filters),
  shipmentsByStatus: useDashboardSection(fetchShipmentsByStatus, filters),
  shipmentsByType: useDashboardSection(fetchShipmentsByType, filters),
  topMerchants: useDashboardSection(
    fetchTopMerchants,
    filters,
    showTopMerchants,
  ),
  operatorsPerf: useDashboardSection(fetchOperatorsPerformance, filters),
  timing: useDashboardSection(fetchTimingSummary, filters),
  slaTrend: useDashboardSection(fetchSlaTrend, filters),
  serviceQuality: useDashboardSection(fetchServiceQuality, filters),
  costSummary: useDashboardSection(fetchCostSummary, filters),
  costByOp: useDashboardSection(fetchCostByOperator, filters),
  costTrend: useDashboardSection(fetchCostTrend, filters),
  geography: useDashboardSection(fetchGeography, filters),
});

// Marca de "última actualización": se refresca cuando el resumen termina de
// cargar. Como orders cachea 2–15 min, ayuda a entender que los datos pueden no
// ser de este instante.
const DashboardHeader = ({ hasSummaryData }: { hasSummaryData: boolean }) => {
  const [lastUpdated, setLastUpdated] = useState<DateTime | null>(null);
  useEffect(() => {
    if (hasSummaryData) setLastUpdated(DateTime.now());
  }, [hasSummaryData]);

  return (
    <Box
      display="flex"
      alignItems="baseline"
      justifyContent="space-between"
      flexWrap="wrap"
      mb={2}
    >
      <Typography variant="h4" fontWeight={700}>
        Inicio
      </Typography>
      {lastUpdated && (
        <Typography variant="caption" color="text.secondary">
          Última actualización:{" "}
          {lastUpdated.setLocale(LOCALE).toFormat("dd/MM/yyyy HH:mm")}
        </Typography>
      )}
    </Box>
  );
};

const ExecutiveSummaryGroup = ({
  summary,
}: {
  summary: ISection<IDashboardSummary>;
}) => (
  <DashboardSectionGroup
    title="Resumen ejecutivo"
    subtitle="Indicadores clave del período seleccionado"
  >
    <DashboardSectionRow widths={[12]}>
      <DashboardSectionContainer
        title="Resumen ejecutivo"
        hideTitle
        disableCard
        loading={summary.loading}
        error={summary.error}
        empty={summary.data === null}
        onRetry={summary.retry}
        skeletonHeight={120}
      >
        {summary.data ? <ExecutiveSummary data={summary.data} /> : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
  </DashboardSectionGroup>
);

interface IOperationStatusGroupProps {
  shipmentsByStatus: ISection<IShipmentByStatus[]>;
  shipmentsByType: ISection<IShipmentByType[]>;
  topMerchants: ISection<ITopMerchant[]>;
  showTopMerchants: boolean;
}

const OperationStatusGroup = ({
  shipmentsByStatus,
  shipmentsByType,
  topMerchants,
  showTopMerchants,
}: IOperationStatusGroupProps) => (
  <DashboardSectionGroup
    title="Estado general de la operación"
    subtitle={
      showTopMerchants
        ? "Distribución por estado logístico, tipo de envío y merchant"
        : "Distribución por estado logístico y tipo de envío"
    }
  >
    {/*
      Alto fijo de 366px: valor literal de las tarjetas en Figma. Cuando la
      entidad no tiene merchants, Top merchants no se muestra y las dos tarjetas
      restantes ocupan 6/6 en vez de 4/4/4 (sin dejar hueco en el grid de 12).
    */}
    <DashboardSectionRow widths={showTopMerchants ? [4, 4, 4] : [6, 6]}>
      <DashboardSectionContainer
        title="Envíos en curso por estado"
        loading={shipmentsByStatus.loading}
        error={shipmentsByStatus.error}
        empty={!shipmentsByStatus.data?.length}
        onRetry={shipmentsByStatus.retry}
        minHeight={366}
        // TODO(producto): Figma muestra un botón "Ver detalle" en el header
        // de esta tarjeta. Falta definir su destino (¿navegar a /ordenes
        // filtrado por estado? ¿abrir un modal?) antes de cablear un onClick
        // real — se deja visible pero deshabilitado para no inventar un
        // comportamiento.
        headerAction={
          <Tooltip title="Pendiente: definir destino con producto. Ver análisis de la branch.">
            <span>
              <Button size="small" disabled>
                Ver detalle
              </Button>
            </span>
          </Tooltip>
        }
      >
        {shipmentsByStatus.data ? (
          <ShipmentsByStatus data={shipmentsByStatus.data} />
        ) : null}
      </DashboardSectionContainer>
      <DashboardSectionContainer
        title="Distribución por tipo de envío"
        loading={shipmentsByType.loading}
        error={shipmentsByType.error}
        empty={!shipmentsByType.data?.length}
        onRetry={shipmentsByType.retry}
        minHeight={366}
      >
        {shipmentsByType.data ? (
          <ShipmentsByType data={shipmentsByType.data} />
        ) : null}
      </DashboardSectionContainer>
      {showTopMerchants && (
        <DashboardSectionContainer
          title="Top merchants por envíos"
          loading={topMerchants.loading}
          error={topMerchants.error}
          empty={!topMerchants.data?.length}
          onRetry={topMerchants.retry}
          minHeight={366}
        >
          {topMerchants.data ? <TopMerchants data={topMerchants.data} /> : null}
        </DashboardSectionContainer>
      )}
    </DashboardSectionRow>
  </DashboardSectionGroup>
);

const OperatorsPerformanceGroup = ({
  operatorsPerf,
}: {
  operatorsPerf: ISection<IOperatorPerformance[]>;
}) => (
  <DashboardSectionGroup
    title="Performance de operadores logísticos"
    subtitle="Ranking comparativo: volumen, SLA, calidad y eficiencia"
  >
    <DashboardSectionRow widths={[12]}>
      <DashboardSectionContainer
        title="Performance de operadores logísticos"
        hideTitle
        loading={operatorsPerf.loading}
        error={operatorsPerf.error}
        empty={!operatorsPerf.data?.length}
        onRetry={operatorsPerf.retry}
        skeletonHeight={200}
      >
        {operatorsPerf.data ? (
          <OperatorsPerformance data={operatorsPerf.data} />
        ) : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
  </DashboardSectionGroup>
);

interface ITimingQualityGroupProps {
  timing: ISection<ITimingSummary>;
  totalOrders: number | null;
  slaTrend: ISection<ISlaTrendItem[]>;
  serviceQuality: ISection<IServiceQuality>;
}

const TimingQualityGroup = ({
  timing,
  totalOrders,
  slaTrend,
  serviceQuality,
}: ITimingQualityGroupProps) => (
  <DashboardSectionGroup
    title="Tiempos, eficiencia y calidad de servicio"
    subtitle="Performance temporal y cumplimiento de SLA"
  >
    <DashboardSectionRow widths={[12]}>
      <DashboardSectionContainer
        title="Métricas de tiempo"
        hideTitle
        disableCard
        loading={timing.loading}
        error={timing.error}
        empty={timing.data === null}
        onRetry={timing.retry}
        skeletonHeight={120}
      >
        {timing.data ? (
          <TimingKpis data={timing.data} totalOrders={totalOrders} />
        ) : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
    {/* Alto fijo de 367px: valor literal de Figma para este par. */}
    <DashboardSectionRow widths={[8, 4]}>
      <DashboardSectionContainer
        title="Evolución de SLA y entregas exitosas"
        loading={slaTrend.loading}
        error={slaTrend.error}
        empty={!slaTrend.data?.length}
        onRetry={slaTrend.retry}
        skeletonHeight={240}
        minHeight={367}
      >
        {slaTrend.data ? <SlaTrend data={slaTrend.data} /> : null}
      </DashboardSectionContainer>
      <DashboardSectionContainer
        title="Calidad de servicio"
        loading={serviceQuality.loading}
        error={serviceQuality.error}
        empty={serviceQuality.data === null}
        onRetry={serviceQuality.retry}
        minHeight={367}
      >
        {serviceQuality.data ? (
          <ServiceQuality data={serviceQuality.data} />
        ) : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
  </DashboardSectionGroup>
);

interface ICostsGroupProps {
  costSummary: ISection<ICostSummary>;
  costByOp: ISection<ICostByOperator[]>;
  costTrend: ISection<ICostTrendItem[]>;
  costTrendTitle: string;
  // País activo (interno): define la moneda de todos los montos de costos.
  country?: string;
}

const CostsGroup = ({
  costSummary,
  costByOp,
  costTrend,
  costTrendTitle,
  country,
}: ICostsGroupProps) => (
  <DashboardSectionGroup
    title="Costos logísticos"
    subtitle="Eficiencia económica por operador y evolución"
  >
    {/* Alto fijo de 338px: valor literal de Figma para este par. */}
    <DashboardSectionRow widths={[4, 8]}>
      <DashboardSectionContainer
        title="Costo total logístico"
        loading={costSummary.loading}
        error={costSummary.error}
        empty={costSummary.data === null}
        onRetry={costSummary.retry}
        skeletonHeight={100}
        minHeight={338}
      >
        {costSummary.data ? (
          <CostSummary
            data={costSummary.data}
            costByOperator={costByOp.data}
            country={country}
          />
        ) : null}
      </DashboardSectionContainer>
      <DashboardSectionContainer
        title="Costo promedio por operador"
        loading={costByOp.loading}
        error={costByOp.error}
        empty={!costByOp.data?.length}
        onRetry={costByOp.retry}
        minHeight={338}
      >
        {costByOp.data ? (
          <CostByOperator data={costByOp.data} country={country} />
        ) : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
    <DashboardSectionRow widths={[12]}>
      <DashboardSectionContainer
        title={costTrendTitle}
        loading={costTrend.loading}
        error={costTrend.error}
        empty={!costTrend.data?.length}
        onRetry={costTrend.retry}
      >
        {/*
          TODO(negocio): CostTrend ya soporta `targetCostPerOrder` (línea
          punteada "Target" del diseño), pero no hay ninguna fuente para ese
          valor hoy: no está en ICostSummary/ICostTrendItem ni existe como
          constante de negocio en el proyecto. No se inventa un número — falta
          que backend/producto defina de dónde sale el objetivo (endpoint
          propio, campo nuevo, o constante fija) antes de pasarlo acá.
        */}
        {costTrend.data ? (
          <CostTrend data={costTrend.data} country={country} />
        ) : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
  </DashboardSectionGroup>
);

const GeographyGroup = ({
  geography,
  provinceLabel,
}: {
  geography: ISection<IGeographyItem[]>;
  provinceLabel: IProvinceLabel;
}) => (
  <DashboardSectionGroup
    title="Distribución geográfica"
    subtitle={`Ranking de envíos por ${provinceLabel.singular.toLowerCase()}`}
  >
    {/*
      V2 (ver docs/DASHBOARD_GEOGRAPHY_V2.md): acá iba el mapa coroplético
      (GeographyMap, hoy comentado). Se reemplaza por la tabla-ranking
      estilizada a ancho completo — más simple y sin depender de geometría de
      provincias, que además difiere por país (AR usa provincias; CO/PA usan
      zonas/códigos DANE). El mapa vuelve en V2 con un selector país→geometría.
    */}
    <DashboardSectionRow widths={[12]}>
      <DashboardSectionContainer
        title="Ranking por total de envíos"
        loading={geography.loading}
        error={geography.error}
        empty={!geography.data?.length}
        onRetry={geography.retry}
        skeletonHeight={200}
      >
        {geography.data ? (
          <Geography data={geography.data} provinceLabel={provinceLabel} />
        ) : null}
      </DashboardSectionContainer>
    </DashboardSectionRow>
  </DashboardSectionGroup>
);

interface IMetricsDashboardContentProps {
  filters: IDashboardFilters;
  applyFilters: (_newFilters: Partial<IDashboardFilters>) => void;
  clearFilters: () => void;
  entity?: string;
  provinceLabel: IProvinceLabel;
}

const MetricsDashboardContent = ({
  filters,
  applyFilters,
  clearFilters,
  entity,
  provinceLabel,
}: IMetricsDashboardContentProps) => {
  const showTopMerchants = entityHasMerchants(entity);
  const showMerchantFilter = entityHasMerchantFilter(entity);
  const showClientFilter = entityHasClientFilter(entity);
  const showCountryFilter = entityHasCountryFilter(entity);
  const sections = useDashboardSections(filters, showTopMerchants);
  const { options: filterOptions } = useDashboardFilterOptions(filters);
  const totalOrders = sections.summary.data
    ? sections.summary.data.totalOrders
    : null;

  const dashboardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = async () => {
    if (!dashboardRef.current || exporting) return;
    setExporting(true);
    try {
      await exportDashboardPdf(dashboardRef.current, {
        title: "Dashboard de Métricas",
        subtitle: buildPdfSubtitle(filters, filterOptions, provinceLabel),
        fileName: `dashboard-metricas-${filters.from}_${filters.to}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <DashboardHeader hasSummaryData={Boolean(sections.summary.data)} />
      <DashboardFilterBar
        filters={filters}
        options={filterOptions}
        showMerchantFilter={showMerchantFilter}
        showClientFilter={showClientFilter}
        showCountryFilter={showCountryFilter}
        onApply={applyFilters}
        onClear={clearFilters}
        onExportPdf={handleExportPdf}
        exporting={exporting}
        provinceLabel={provinceLabel}
      />
      {/*
        Stack spacing={3} (24px) entre grupos: en Figma el gap vertical es
        uniforme (título→contenido y contenido→próximo título miden siempre
        24px), así que un solo spacing acá alcanza — los grupos y sus filas
        ya no traen su propio margen para no duplicarlo.
      */}
      <Stack spacing={3} ref={dashboardRef}>
        <ExecutiveSummaryGroup summary={sections.summary} />
        <OperationStatusGroup
          shipmentsByStatus={sections.shipmentsByStatus}
          shipmentsByType={sections.shipmentsByType}
          topMerchants={sections.topMerchants}
          showTopMerchants={showTopMerchants}
        />
        <OperatorsPerformanceGroup operatorsPerf={sections.operatorsPerf} />
        <TimingQualityGroup
          timing={sections.timing}
          totalOrders={totalOrders}
          slaTrend={sections.slaTrend}
          serviceQuality={sections.serviceQuality}
        />
        <CostsGroup
          costSummary={sections.costSummary}
          costByOp={sections.costByOp}
          costTrend={sections.costTrend}
          costTrendTitle={buildCostTrendTitle(filters.from, filters.to)}
          country={filters.country}
        />
        <GeographyGroup
          geography={sections.geography}
          provinceLabel={provinceLabel}
        />
      </Stack>
    </Box>
  );
};

const MetricsDashboard = () => {
  const {
    currentUser: { entity, entityInfo },
    selectedMarketplace,
  } = useContext(UserContext);
  // El nombre de la división geográfica (Provincia/Departamento) depende del
  // país de la entidad; se resuelve una vez y se propaga a las secciones.
  const provinceLabel = getProvinceLabel(entityInfo?.country);
  // El usuario interno es cross a todas las entidades: el BFF resuelve el scope
  // global sin necesidad de seleccionar un marketplace. El resto de entidades
  // resuelve su clientId por sí mismo (marketplace/ecommerce) o vía el marketplace
  // seleccionado en el navbar (merchant).
  const { filters, applyFilters, clearFilters } = useDashboardFilters(entity);

  // Tras un login fresco, el merchant resuelve su marketplace de forma
  // asíncrona (UserContext.getIntegrationsOfMerchant → setSelectedMarketplace).
  // Hasta que ese marketplaceId exista, montar el contenido dispararía los ~12
  // fetches del dashboard sin scope: una ráfaga de requests tempranas que fallan
  // sí o sí y llenan la pantalla de errores confusos. Demoramos el montaje —y
  // por lo tanto todos los fetches— mostrando un loader. Cuando el context
  // resuelve el marketplace, re-renderiza a este consumidor con el filtro ya
  // poblado y las secciones fetchean una sola vez. `selectedMarketplace` es la
  // señal reactiva que fuerza ese re-render (el localStorage se puebla antes).
  const awaitingMerchantMarketplace =
    entity === MERCHANT && !filters.marketplaceId && !selectedMarketplace;

  if (awaitingMerchantMarketplace) {
    return (
      <Box sx={{ p: 3 }}>
        <DashboardHeader hasSummaryData={false} />
        <Loader size="md" />
      </Box>
    );
  }

  return (
    <MetricsDashboardContent
      filters={filters}
      applyFilters={applyFilters}
      clearFilters={clearFilters}
      entity={entity}
      provinceLabel={provinceLabel}
    />
  );
};

export default MetricsDashboard;
